import React, { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import {
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  Fab,
  IconButton,
  makeStyles,
} from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import AddPhotoAlternate from '@material-ui/icons/AddPhotoAlternate';
import { FormattedMessage, useIntl } from 'react-intl';

const useStyles = makeStyles((theme) => ({
  grid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  thumbnailWrapper: {
    position: 'relative',
    width: 136,
    height: 136,
    flexShrink: 0,
    '&:hover $removeButton': {
      opacity: 1,
    },
  },
  thumbnail: {
    width: '100%',
    height: '136px !important',
    objectFit: 'cover',
    borderRadius: 4,
    cursor: 'pointer',
  },
  removeButton: {
    position: 'absolute',
    right: -8,
    top: -8,
    width: 20,
    height: 20,
    minHeight: 20,
    boxShadow: theme.shadows[2],
    opacity: 0,
    transition: 'opacity 0.2s',
    '@media (hover: none)': {
      opacity: 1,
    },
    '& .MuiSvgIcon-root': {
      fontSize: 14,
    },
  },
  imageButtonFill: {
    display: 'block',
    width: '100%',
    height: '100%',
    borderRadius: 4,
    overflow: 'hidden',
  },
  lightboxContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 !important',
    backgroundColor: '#000',
    position: 'relative',
    minHeight: 300,
  },
  lightboxImage: {
    maxWidth: '100%',
    maxHeight: '80vh !important',
    display: 'block',
  },
  lightboxCounter: {
    position: 'absolute',
    bottom: 16,
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(0,0,0,0.5)',
    color: '#fff',
    borderRadius: 12,
    padding: '2px 10px',
    fontSize: 13,
  },
  lightboxNav: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    padding: 8,
    color: '#fff',
    backgroundColor: 'rgba(0,0,0,0.4)',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.6)',
    },
    '& .MuiSvgIcon-root': {
      fontSize: 20,
    },
  },
  lightboxPrev: {
    left: 10,
  },
  lightboxNext: {
    right: 10,
  },
}));

const ImageGallery = ({ images, onDelete, onAdd, maxCount, readOnly }) => {
  const classes = useStyles();
  const intl = useIntl();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('xs'));
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const openLightbox = useCallback((index) => setLightboxIndex(index), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  const lightboxPrev = useCallback(() => {
    if (!images.length) return;
    setLightboxIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const lightboxNext = useCallback(() => {
    if (!images.length) return;
    setLightboxIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  React.useEffect(() => {
    if (lightboxIndex === null) return;
    if (!images.length || !images[lightboxIndex]) {
      setLightboxIndex(null);
    }
  }, [images, lightboxIndex]);

  React.useEffect(() => {
    if (lightboxIndex === null) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') lightboxPrev();
      else if (e.key === 'ArrowRight') lightboxNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, lightboxPrev, lightboxNext]);

  // Simple swipe detection
  const touchStartX = useRef(0);

  const onTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const onTouchEnd = useCallback(
    (e) => {
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      if (dx < -50) lightboxNext();
      else if (dx > 50) lightboxPrev();
    },
    [lightboxNext, lightboxPrev],
  );

  const handleDelete = useCallback(() => {
    if (readOnly || lightboxIndex === null || !onDelete) return;
    const idx = lightboxIndex;
    const remainingCount = images.length - 1;
    onDelete(idx);
    if (remainingCount === 0) {
      setLightboxIndex(null);
    } else {
      setLightboxIndex(Math.min(idx, remainingCount - 1));
    }
  }, [readOnly, lightboxIndex, onDelete, images.length]);

  if (!images || images.length === 0) {
    if (!readOnly && onAdd) {
      return (
        <Button variant="outlined" size="small" startIcon={<AddPhotoAlternate />} onClick={onAdd}>
          <FormattedMessage id="complaints.addImages" defaultMessage="Add images" />
        </Button>
      );
    }
    return null;
  }

  return (
    <>
      <div className={classes.grid}>
        {images.map((img, idx) => (
          <div key={img.id} className={classes.thumbnailWrapper}>
            <ButtonBase
              className={classes.imageButtonFill}
              onClick={() => openLightbox(idx)}
              aria-label={intl.formatMessage(
                { id: 'imageGallery.openImage', defaultMessage: 'Open image {index}' },
                { index: idx + 1 },
              )}
            >
              <img src={img.url} alt="" className={classes.thumbnail} />
            </ButtonBase>
            {!readOnly && onDelete && (
              <Fab
                className={classes.removeButton}
                onClick={() => onDelete(idx)}
                size="small"
                color="secondary"
                aria-label={intl.formatMessage({
                  id: 'imageGallery.removeImage',
                  defaultMessage: 'Remove image',
                })}
              >
                <Close />
              </Fab>
            )}
          </div>
        ))}
      </div>

      {!readOnly && onAdd && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddPhotoAlternate />}
          onClick={onAdd}
          disabled={maxCount != null && images.length >= maxCount}
        >
          <FormattedMessage id="complaints.addImages" defaultMessage="Add images" />
        </Button>
      )}

      {lightboxIndex !== null && images[lightboxIndex] && (
        <Dialog open onClose={closeLightbox} maxWidth="md" fullWidth fullScreen={isSmall}>
          <DialogContent
            className={classes.lightboxContent}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <img
              src={images[lightboxIndex].url}
              alt={`${lightboxIndex + 1}`}
              className={classes.lightboxImage}
            />
            {images.length > 1 && (
              <>
                <IconButton
                  className={`${classes.lightboxNav} ${classes.lightboxPrev}`}
                  onClick={lightboxPrev}
                  aria-label={intl.formatMessage({
                    id: 'imageGallery.previousImage',
                    defaultMessage: 'Previous image',
                  })}
                >
                  <ChevronLeft />
                </IconButton>
                <IconButton
                  className={`${classes.lightboxNav} ${classes.lightboxNext}`}
                  onClick={lightboxNext}
                  aria-label={intl.formatMessage({
                    id: 'imageGallery.nextImage',
                    defaultMessage: 'Next image',
                  })}
                >
                  <ChevronRight />
                </IconButton>
                <span className={classes.lightboxCounter}>
                  {lightboxIndex + 1} / {images.length}
                </span>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <span style={{ flex: 1 }} />
            {!readOnly && onDelete && (
              <Button color="secondary" startIcon={<DeleteOutline />} onClick={handleDelete}>
                <FormattedMessage id="imageGallery.delete" defaultMessage="Delete" />
              </Button>
            )}
            <Button onClick={closeLightbox}>
              <FormattedMessage id="imageGallery.close" defaultMessage="Close" />
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

ImageGallery.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      url: PropTypes.string.isRequired,
    }),
  ),
  onDelete: PropTypes.func,
  onAdd: PropTypes.func,
  maxCount: PropTypes.number,
  readOnly: PropTypes.bool,
};

ImageGallery.defaultProps = {
  images: [],
  readOnly: false,
};

export default ImageGallery;
